import { BaseWidget } from './BaseWidget';
import { SliderControl } from '../controls/SliderControl';
import { ColorControl } from '../controls/ColorControl';
import { NumberCssControl } from '../controls/NumberCssControl';
import { IconButtonGroupControl } from '../controls/IconButtonGroupControl';
import InterestsOutlinedIcon from '@mui/icons-material/InterestsOutlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EuroOutlinedIcon from '@mui/icons-material/EuroOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';

/**
 * @typedef {object} IconMapEntry
 * @property {string} label - Dutch display label shown in the icon picker
 * @property {React.ElementType} Component - MUI icon component to render
 */

/**
 * Curated set of common report icons. Keys are stored in widget settings,
 * so they must stay stable — add new entries, never rename existing keys.
 * @type {Record<string, IconMapEntry>}
 */
export const ICON_MAP = {
    arrowUp: { label: 'Pijl omhoog', Component: ArrowUpwardIcon },
    arrowDown: { label: 'Pijl omlaag', Component: ArrowDownwardIcon },
    arrowLeft: { label: 'Pijl naar links', Component: ArrowBackIcon },
    arrowRight: { label: 'Pijl naar rechts', Component: ArrowForwardIcon },
    trendingUp: { label: 'Stijgende trend', Component: TrendingUpIcon },
    trendingDown: { label: 'Dalende trend', Component: TrendingDownIcon },
    check: { label: 'Vinkje', Component: CheckIcon },
    close: { label: 'Kruisje', Component: CloseIcon },
    star: { label: 'Ster', Component: StarOutlineIcon },
    heart: { label: 'Hart', Component: FavoriteBorderIcon },
    lightbulb: { label: 'Gloeilamp', Component: LightbulbOutlinedIcon },
    info: { label: 'Informatie', Component: InfoOutlinedIcon },
    warning: { label: 'Waarschuwing', Component: WarningAmberOutlinedIcon },
    flag: { label: 'Vlag', Component: FlagOutlinedIcon },
    person: { label: 'Persoon', Component: PersonOutlineIcon },
    group: { label: 'Groep', Component: GroupsOutlinedIcon },
    euro: { label: 'Euro', Component: EuroOutlinedIcon },
    thumbUp: { label: 'Duim omhoog', Component: ThumbUpOutlinedIcon },
    thumbDown: { label: 'Duim omlaag', Component: ThumbDownOutlinedIcon },
    target: { label: 'Doelwit', Component: TrackChangesOutlinedIcon },
    home: { label: 'Huis', Component: HomeOutlinedIcon },
    calendar: { label: 'Kalender', Component: CalendarMonthOutlinedIcon },
    chat: { label: 'Spraakwolk', Component: ChatBubbleOutlineIcon },
    location: { label: 'Locatie', Component: PlaceOutlinedIcon },
    trophy: { label: 'Trofee', Component: EmojiEventsOutlinedIcon },
    quote: { label: 'Aanhalingstekens', Component: FormatQuoteIcon },
    flame: { label: 'Vlam', Component: LocalFireDepartmentOutlinedIcon },
    bolt: { label: 'Bliksem', Component: BoltOutlinedIcon },
    tool: { label: 'Gereedschap', Component: BuildOutlinedIcon },
    apartment: { label: 'Flat', Component: ApartmentOutlinedIcon },
};

/** Default ICON_MAP key, shared by the control default and the renderer fallback. */
const DEFAULT_ICON = 'arrowRight';

/**
 * Back-compat: weight was briefly stored as a named step before it became
 * a numeric slider. Maps the old names to their slider equivalents.
 * @type {Record<string, number>}
 */
const LEGACY_WEIGHTS = {
    normal: 0,
    semiBold: 0.6,
    bold: 1.2,
    extraBold: 1.8,
};

/**
 * Resolve a stored weight setting (number, legacy name, or garbage) to a
 * slider value.
 * @param {number | string | undefined} weight
 * @returns {number}
 */
const resolveWeight = (weight) => {
    if (typeof weight === 'number') {
        return weight;
    }
    return LEGACY_WEIGHTS[weight] ?? 0;
};

/**
 * IconWidget — renders one icon from a curated set, with size, color,
 * weight and alignment controls. Purely presentational: no Firestore reads,
 * so it behaves identically in live, template-preview and public-viewer mode.
 *
 * @extends {BaseWidget}
 */
export class IconWidget extends BaseWidget {
    getName() { return 'Icoon'; }
    getIcon() { return <InterestsOutlinedIcon />; }

    getControls() {
        return {
            icon: new IconButtonGroupControl({
                label: 'Icoon',
                description: 'Kies een icoon',
                defaultValue: DEFAULT_ICON,
                options: {
                    className: 'icon-picker-control',
                    buttons: Object.entries(ICON_MAP).map(([value, { label, Component }]) => ({
                        value,
                        icon: <Component />,
                        tooltip: label,
                    })),
                },
            }),
            size: new NumberCssControl({
                label: 'Grootte',
                description: '',
                defaultValue: { value: 32, unit: 'px' },
                options: { min: 8, max: 240 },
            }),
            weight: new SliderControl({
                label: 'Dikte',
                description: 'Naar links voor dunner, naar rechts voor vetter (0 = normaal)',
                defaultValue: 0,
                options: {
                    min: -1,
                    max: 2,
                    step: 0.1,
                },
            }),
            color: new ColorControl({
                label: 'Kleur',
                description: 'Kleur van het icoon',
                defaultValue: '#000000',
            }),
            align: new IconButtonGroupControl({
                label: 'Uitlijning',
                description: '',
                defaultValue: 'left',
                options: {
                    buttons: [
                        { value: 'left', icon: <FormatAlignLeftIcon />, tooltip: 'Links' },
                        { value: 'center', icon: <FormatAlignCenterIcon />, tooltip: 'Midden' },
                        { value: 'right', icon: <FormatAlignRightIcon />, tooltip: 'Rechts' },
                    ],
                },
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {string} [props.icon] - Key into ICON_MAP
 * @param {{ value: number, unit?: string }} [props.size] - Icon size (maps to font-size)
 * @param {number | string} [props.weight] - Emulated font-weight: negative = thinner (erode filter), positive = bolder (outline stroke)
 * @param {string} [props.color] - Icon colour
 * @param {'left'|'center'|'right'} [props.align] - Horizontal alignment within the widget block
 */
const Renderer = ({ icon = DEFAULT_ICON, size, weight = 0, color, align = 'left' }) => {
    const entry = ICON_MAP[icon];

    if (!entry) {
        return <p className="analysis-card-empty">Kies een icoon.</p>;
    }

    const IconComponent = entry.Component;
    const resolvedWeight = resolveWeight(weight);

    // Stroke width is in 24-unit viewBox coordinates, so it scales with the
    // icon automatically. The erode filter works in rendered pixels, so its
    // radius must be scaled by the icon's actual size by hand.
    const sizePx = size?.value ?? 32;
    const erodeRadius = resolvedWeight < 0
        ? Math.round(Math.abs(resolvedWeight) * (sizePx / 24) * 1000) / 1000
        : 0;
    const filterId = erodeRadius > 0
        ? `lr2-icon-erode-${String(erodeRadius).replace('.', '-')}`
        : null;

    // `text-align` positions the inline icon within the full-width widget block.
    return (
        <div style={{ textAlign: align }}>
            {filterId && (
                /* Invisible defs-only svg carrying the erode filter. The id is
                   derived from the radius, so widgets with the same weight and
                   size share an identical (harmlessly duplicated) filter. */
                <svg className="lr2-icon-filter-defs" width="0" height="0" aria-hidden="true" focusable="false">
                    <filter id={filterId}>
                        <feMorphology operator="erode" radius={erodeRadius} />
                    </filter>
                </svg>
            )}
            <IconComponent
                style={{
                    fontSize: size && `${size.value}${size.unit ?? 'px'}`,
                    color: color,
                    filter: filterId ? `url(#${filterId})` : undefined,
                }}
                {...(resolvedWeight > 0 && {
                    stroke: 'currentColor',
                    strokeWidth: resolvedWeight,
                })}
            />
        </div>
    );
};
